import { useEffect, useMemo } from 'react';
import _isNil from 'lodash/isNil';
import { useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { Candidate, CandidateStatus } from '../../../../firebase/firestore/documents/candidate';
import { Collections } from '../../../../firebase/firestore/collections';
import { useQuerySnapshot } from '../../../../firebase/firestore/hooks';
import { Query } from '../../../../firebase/firestore/query/query';
import { useCurrentUserProfile } from '../../../../app/hooks/useCurrentUserProfile';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { Criteria } from '../../../../firebase/firestore/query/criteria';
import { ModernHireStatusList } from '../../../../firebase/firestore/documents/modernHireTypes';
import { DEFAULT_FILTER_CANDIDATE_BY_DAYS } from '../../../../utils/candidatesUtils';
import { checkIfUserRestrictedInPositions, filteredCandidatesByRestriction } from '../../../../services/userService';

const buildQuery = (
  filterCandidateByDays: number,
  account?: string,
  seekerId?: string,
  isRequsitionEnabled?: boolean,
  positionIdFromParams?: string,
  isSigleCandidatePage?: boolean,
): Criteria[] | null => {
  // this is the logic that pulls single candidate for a single position, currrently removed
  // if (isPositionCandidatesPage() && positionIdFromParams) {
  //   return [
  //     Query.field<Candidate>('monetized').equals(true),
  //     Query.field<Candidate>('seeker').equals(seekerId),
  //     Query.field<Candidate>('position').equals(positionIdFromParams),
  //     Query.field<Candidate>('account').equals(account),
  //     Query.field<Candidate>('positionOpen').equals(true),
  //   ];
  // }

  // if (isCandidateOnDashboard()) {
  //   return [
  //     Query.field<Candidate>('monetized').equals(true),
  //     Query.field<Candidate>('seeker').equals(seekerId),
  //     Query.field<Candidate>('account').equals(account),
  //     Query.field<Candidate>('positionOpen').equals(true),
  //   ];
  // }

  if (isSigleCandidatePage) {
    const criteria: Criteria[] = [
      Query.field<Candidate>('seeker').equals(seekerId),
      Query.field<Candidate>('account').equals(account),
      Query.field<Candidate>('positionOpen').equals(true),
    ];
    if (isRequsitionEnabled) {
      const fromDate = moment(moment().subtract(filterCandidateByDays, 'days').format('YYYY-MM-DDT00:00:00')).toDate();
      criteria.push(Query.field('computedStatus.lastUpdate').greaterThan(fromDate));
    }
    return criteria;
  }

  if (isRequsitionEnabled) {
    const fromDate = moment(moment().subtract(filterCandidateByDays, 'days').format('YYYY-MM-DDT00:00:00')).toDate();
    return [
      Query.field<Candidate>('monetized').equals(true),
      Query.field<Candidate>('seeker').equals(seekerId),
      Query.field<Candidate>('account').equals(account),
      Query.field<Candidate>('positionOpen').equals(true),
      Query.field('computedStatus.lastUpdate').greaterThan(fromDate),
    ];
  }

  return [
    Query.field<Candidate>('monetized').equals(true),
    Query.field<Candidate>('seeker').equals(seekerId),
    Query.field<Candidate>('account').equals(account),
    Query.field<Candidate>('positionOpen').equals(true),
  ];
};

export const useSeekerCandidates = (
  seekerId?: string,
  currentPage?: string,
  isSigleCandidatePage?: boolean,
): Readonly<Candidate>[] | undefined => {
  const { positionId } = useParams<{ positionId: string }>();
  const { currentUserProfile } = useCurrentUserProfile();
  const { setSelectedCandidates } = useStoreActions((a) => a.candidate);
  const isRequsitionEnabled = useStoreState((state) => state.app.accounts?.configuration?.requisitionEnabled);
  const filteredCandidateByDays = useStoreState((state) => state.app.accounts?.configuration?.filterCandidateByDays);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const appUserId = useStoreState((s) => s.app.user?.id);
  const accounts = useStoreState((s) => s.app.accounts);
  const restrictPositions = useStoreState((s) => s.app.user?.restrictPositions);

  const superOwners = accounts?.superOwners ?? [];

  const isUserRestrictedInPositions = checkIfUserRestrictedInPositions({
    appOwnersId,
    appUserId,
    superOwners,
    isUserRestricted: restrictPositions,
  });

  const candidates = useQuerySnapshot<Readonly<Candidate>>(
    Collections.CANDIDATES,
    buildQuery(
      filteredCandidateByDays || DEFAULT_FILTER_CANDIDATE_BY_DAYS,
      currentUserProfile?.account,
      seekerId,
      isRequsitionEnabled,
      positionId,
      isSigleCandidatePage,
    ),
  );

  const checkModernHireStatus = (condition: boolean) => {
    return currentPage === 'candidate' ? true : condition;
  };

  const filteredCandidates = useMemo(() => {
    const filteredCandidatesAfterUserRestriction = filteredCandidatesByRestriction(
      candidates,
      isUserRestrictedInPositions,
      appUserId,
    );

    return isSigleCandidatePage
      ? filteredCandidatesAfterUserRestriction
      : filteredCandidatesAfterUserRestriction
          ?.filter((value) => {
            return !(value.accountExcluded && (value.computedStatus?.status === CandidateStatus.Blacklisted || value.dismissed));
          })
          .filter(
            (c) =>
              ([true, false].includes(c.modernHireEnable) &&
                c.modernHire &&
                checkModernHireStatus(c.modernHire.status === ModernHireStatusList.ASSESSMENT_PASSED)) ||
              c.positionalModernHireDisabled ||
              (_isNil(c.modernHireEnable) && _isNil(c.modernHire)),
          );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [candidates, isUserRestrictedInPositions, appUserId, isSigleCandidatePage, currentPage]);

  useEffect(() => {
    if (currentUserProfile && !_isNil(filteredCandidates)) {
      setSelectedCandidates(filteredCandidates);
    }
  }, [filteredCandidates, currentUserProfile, setSelectedCandidates]);

  return filteredCandidates;
};
